import { type Profile, type Print } from 'app/store/types';

export const managedPrintGroupTypes = ['shared', 'guest'] as const;
export const printGroupTypes = ['personal', ...managedPrintGroupTypes] as const;

export const isDelegatedToMePrint = (print: Print, profileId: Profile['id']) => {
	const delegateUserIds = print.acl.users.filter(({ role }) => role !== 'owner').map(({ name }) => name);
	return delegateUserIds.includes(profileId);
};

export const printPreferencesMenuOptions = {
	color: ['AUTO', 'COLOR', 'BLACK'],
	collate: ['', 'False', 'True'],
	offset: ['', 'False', 'True'],
	layout: ['', 'NORMAL', 'DUPLEX', 'BOOKLET4IN1'],
	binding: ['', 'LEFT', 'RIGHT', 'TOP'],
	staple: ['', 'CORNER', 'ANY2POINT'],
	fold: ['', 'CENTERFOLDIN'],
	punch: ['', '2', '3'],
} as const;

// this is coincidentally the same list as `printPreferencesMenuOptions` because the only other option on the screen is `copies` and you can't disable editable for that
const printPreferencesEditableNames = [
	...(Object.keys(printPreferencesMenuOptions) as (keyof typeof printPreferencesMenuOptions)[]),
] as const;

type PrintPreferenceEditableName = (typeof printPreferencesEditableNames)[number];

export const isEditablePrintPreference = (
	printPreferenceName: keyof PrintPreferences,
): printPreferenceName is PrintPreferenceEditableName => {
	return printPreferencesEditableNames.some((name) => name === printPreferenceName);
};

export type Option = { name: string; value: string };

export type PrintPreferences = {
	guestDuration: number;
	printJobExpirationDuration: number;
	guestQueueExpirationDuration: number;
	copies: number;
	headers: Option[];
} & {
	[key in keyof typeof printPreferencesMenuOptions]: (typeof printPreferencesMenuOptions)[key][number];
} & {
	[key in PrintPreferenceEditableName as `${key}_editable`]: boolean;
};

export const DEFAULT_PRINT_PREFERENCES = {
	guestDuration: 7 * 24 * 60 * 60,
	printJobExpirationDuration: 7 * 24 * 60 * 60,
	guestQueueExpirationDuration: 7 * 24 * 60 * 60,
	copies: 1,
	headers: [] as PrintPreferences['headers'],
	...Object.fromEntries(Object.entries(printPreferencesMenuOptions).map(([key, value]) => [key, value[0]])),
	...Object.fromEntries(printPreferencesEditableNames.map((name) => [`${name}_editable`, true])),
} as PrintPreferences;

export const editablePrintPreferenceIsDisabled = (preferences: PrintPreferences) => (name: keyof PrintPreferences) =>
	isEditablePrintPreference(name) && preferences[`${name}_editable`] === false;

export const validPrintPreferences = (preferences: PrintPreferences) => {
	// CHANGEME::should I just filter out fully-empty headers options? (if so this check should wait for that)
	const hasEmptyHeaderName = preferences.headers.some((header) => header.name.trim() === '');

	if (hasEmptyHeaderName) {
		return false;
	}

	return true;
};
